/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useMemo } from 'react';
import Select from '../../../../components/form/elements/select';
import { FormComponent } from '../../../../components/form';
import { map } from 'rxjs/operators';
import { useParams } from 'react-router-dom';
import { useService } from '../../../../services/services';
import { useTranslation } from 'react-i18next';
import Disabled from '../../../../components/form/elements/disabled';
import Watch from '../../../../components/form/elements/watch';
import AnimalType from '../../../../enums/animal-type';
import AnimalGenders from '../../../../enums/animal-genders';
import { AnimalEventAnimal } from '../../../../interfaces/animal-event';

interface Props {
  addRow: (a: any, getValues?: any, setValue?: any) => void;
  eventType?: string;
}

function useFormTop({ addRow, eventType }: Props) {
  const { farmId } = useParams<{
    farmId: string;
  }>();

  const { t } = useTranslation();

  const transKeyPrefix = 'pages.livestock.animals_and_events.';
  const tp = useCallback(
    (key: string, params?: any) => t(transKeyPrefix.concat(key), params),
    [t]
  );
  const { animalService } = useService();

  const gender = eventType === AnimalType.pregnancy ? AnimalGenders.FEMALE : '';

  return useMemo<FormComponent[]>(
    () => [
      Select('species', {
        asyncOptions: (value, _loadedOptions, { page, setValue }) => {
          return animalService.species(farmId, { value }).pipe(
            map((response) => {
              if (response.length === 1) {
                setValue('species', response[0]);
              }

              return {
                hasMore: false,
                options: response,
                additional: { page },
              };
            })
          );
        },
        label: tp('animals.species'),
        column: true,
        clearable: false,
      }),
      Select('barn_ldc_nr', {
        asyncOptions: (_value, _loadedOptions, { setValue }) => {
          return animalService
            .barns(farmId, {
              add_location_to_label: true,
            })
            .pipe(
              map((barns) => {
                if (barns.length === 1) {
                  setValue('barn_ldc_nr', {
                    label: barns[0].label,
                    value: barns[0].value,
                  });
                }

                return {
                  hasMore: false,
                  options: barns.map((barn) => ({
                    ...barn,
                    label: barn.label,
                    value: barn.value,
                  })),
                  additional: false,
                };
              })
            );
        },
        label: tp('animals.barn'),
        column: true,
        clearable: false,
      }),
      Disabled(
        Watch(
          Select('animal_id', {
            asyncOptions: (value, loadedOptions, { page, getValues }) => {
              const barn_ldc_nr = getValues('barn_ldc_nr')?.value;
              const species = getValues('species')?.value;
              let alreadyAddedAnimalIds = [];

              if (eventType === AnimalType.weighing) {
                alreadyAddedAnimalIds = getValues("weighingsAndAnimals").map(
                  (animal) => {
                    return animal.animalId
                  }
                );
              } else {
                alreadyAddedAnimalIds = getValues("animals").map(
                  (animal: AnimalEventAnimal) => animal.id
                );
              }

              return animalService
                .index(farmId, {
                  page,
                  barn_ldc_nr,
                  species,
                  gender,
                  animal_id_last4: !isNaN(Number(value)) ? value : '',
                  animal_name: isNaN(Number(value)) ? value : '',
                })
                .pipe(
                  map((response) => {
                    const filteredOptions = response.data.filter(
                      (animal) => !alreadyAddedAnimalIds.includes(animal.id)
                    );

                    return {
                      hasMore: response.last_page > response.current_page,
                      options: filteredOptions.map((animal) => ({
                        label: animal.animal_id,
                        value: animal.id,
                        ...animal,
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
            },
            label: tp('events.grazing.add_animal'),
            cacheOn: () => {
              const baseCacheOnValues = ['species.value', 'barn_ldc_nr.label'];

              return eventType === AnimalType.weighing
                ? [...baseCacheOnValues, 'weighingsAndAnimals']
                : [...baseCacheOnValues, 'animals']
            },
            column: true,
            clearable: false,
          }),
          ({ value, getValues, setValue }) => {
            if (!!value.value) {
              addRow(value, getValues, setValue);
            }
          }
        ),
        {
          watchOn: ['species', 'barn_ldc_nr'],
          callback: (watchValues) => {
            const species = watchValues[0]?.value;
            const barn_ldc_nr = watchValues[1]?.label;

            return !(species && barn_ldc_nr);
          },
        }
      ),
    ],
    []
  );
}

export default useFormTop;
